import { makeAssistantToolUI, useThread, useThreadRuntime } from '@assistant-ui/react';
import { AlertTriangleIcon, WrenchIcon } from 'lucide-react';
import { Button } from '@shadcn/ui/components/ui/button';
import { cn } from '@shadcn/ui/lib/utils';
import { useState } from 'react';
import { z } from 'zod';
import { Textarea } from '@shadcn/ui/components/ui/textarea';
import { useNavigate } from 'react-router-dom';

export interface DisplayErrorProps {
    args: ErrorInputSchema;
    result: ErrorOutputSchema;
    status: 'running' | 'complete' | 'incomplete' | 'requires-action';
}

export type ErrorInputSchema = z.infer<typeof errorInputSchema>;
export const errorInputSchema = z.object({
    error: z.object({
        message: z.string().describe('The error that occurred'),
        stack: z.string().describe('The stack trace of the error'),
    }),
    fixAttempts: z.number().optional().describe('Number of times the user has tried to fix this error'),
});

export type ErrorOutputSchema = z.infer<typeof errorOutputSchema>;
export const errorOutputSchema = z.object({
    result: z.string().describe('The error message.'),
    fixAttempts: z.number().optional().describe('Number of times the user has tried to fix this error'),
});

export const DisplayError = ({ args, result, status }: DisplayErrorProps) => {
    const thread = useThreadRuntime();
    const [isFixing, setIsFixing] = useState(false);
    const [additionalInfo, setAdditionalInfo] = useState('');
    const { error, fixAttempts = 0 } = args;
    const navigate = useNavigate();
    const handleFixIt = async () => {
        if (isFixing) return;
        setIsFixing(true);

        // Instead of using addResult directly, send a message with 'fix' in the text
        // This will be picked up by the runtime provider
        await thread.append({
            role: 'user',
            content: [
                {
                    type: 'text',
                    text: `Fix the following error: ${error.message}`,
                },
            ],
        });
        setIsFixing(false);
    };

    const handleAdditionalInfoSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!additionalInfo.trim()) return;
        setIsFixing(true);
        await thread.append({
            role: 'user',
            content: [
                {
                    type: 'text',
                    text: `Additional info about the error: ${additionalInfo}`,
                },
            ],
        });
        setIsFixing(false);
        setAdditionalInfo('');
    };

    return (
        <div className="mb-4 flex w-full flex-col gap-3 rounded-lg border border-destructive bg-destructive/5 py-4 px-4 text-sm">
            <div className="flex items-center gap-2">
                <AlertTriangleIcon className="size-5 text-destructive" />
                <p className="font-medium text-destructive">An error occurred</p>
            </div>

            <div className="flex flex-col gap-2">
                <pre className="whitespace-pre-wrap text-sm text-muted-foreground">{error.message}</pre>
            </div>

            {fixAttempts >= 3 ? (
                <div className="flex flex-col gap-3">
                    <div className="text-sm font-medium text-destructive">
                        You've tried to fix this {fixAttempts} times and it's still not working.
                    </div>

                    <form onSubmit={handleAdditionalInfoSubmit} className="flex flex-col gap-3">
                        <Textarea
                            className="min-h-[80px] resize-none"
                            placeholder="Describe what you are trying to fix or any extra details..."
                            value={additionalInfo}
                            onChange={(e) => setAdditionalInfo(e.target.value)}
                            disabled={isFixing}
                        />
                        <div className="flex gap-2">
                            <Button
                                type="submit"
                                disabled={isFixing || !additionalInfo.trim() || thread.getState().isRunning}
                                size="sm"
                                variant="default"
                            >
                                {isFixing ? 'Fixing...' : 'Fix it'}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/new/home')}>
                                Start a new chat
                            </Button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="flex items-center justify-end mt-2">
                    <Button
                        onClick={handleFixIt}
                        className={cn('gap-2', isFixing ? 'opacity-80 pointer-events-none' : '')}
                        disabled={isFixing || thread.getState().isRunning}
                        variant="outline"
                        size="sm"
                    >
                        <WrenchIcon className="size-4" />
                        <span>{isFixing ? 'Fixing...' : 'Fix it'}</span>
                    </Button>
                </div>
            )}
        </div>
    );
};

export const DisplayErrorToolUI = makeAssistantToolUI<ErrorInputSchema, ErrorOutputSchema>({
    toolName: 'displayError',
    render: ({ args, result, status }) => DisplayError({ args, result, status: status.type }),
});

export const displayErrorToolsUI = [DisplayErrorToolUI];
